import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type Project } from '@/lib/ProjectsManager';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { ChatPane, type ChatPaneRef } from '@/components/Shakespeare/ChatPane';
import { PreviewPane, type PreviewPaneRef } from '@/components/Shakespeare/PreviewPane';
import { ProjectSidebar } from '@/components/ProjectSidebar';
import { ProjectDetailsDialog } from '@/components/ProjectDetailsDialog';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Eye, Code, Menu, LayoutGrid, ChevronUp, ChevronDown, Compass } from 'lucide-react';
import { ActionsMenu } from '@/components/ActionsMenu';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GitStatusIndicator } from '@/components/GitStatusIndicator';
import { StarButton } from '@/components/StarButton';
import { useBuildProject } from '@/hooks/useBuildProject';
import { useIsProjectPreviewable } from '@/hooks/useIsProjectPreviewable';
import { useConsoleError } from '@/hooks/useConsoleError';
import { AfterHoursLogo } from '@/components/AfterHoursLogo';
import { cn } from '@/lib/utils';

export function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'preview' | 'code'>('preview');
  const [mobileView, setMobileView] = useState<'chat' | 'preview' | 'code'>('chat');
  const [isAILoading, setIsAILoading] = useState(false);
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);

  // Projects drawer state (replaces fixed sidebar)
  const [isProjectsDrawerOpen, setIsProjectsDrawerOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Use console error state from provider
  const { consoleError, dismissConsoleError, clearErrors } = useConsoleError();

  const projectsManager = useProjectsManager();
  const chatPaneRef = useRef<ChatPaneRef>(null);
  const previewPaneRef = useRef<PreviewPaneRef>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const build = useBuildProject(projectId!);
  const { data: isPreviewable = false } = useIsProjectPreviewable(projectId!);

  const loadProject = useCallback(async () => {
    if (!projectId) return;

    try {
      const projectData = await projectsManager.getProject(projectId);
      setProject(projectData);
      // Clear console messages and error state when switching projects
      clearErrors();
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, projectsManager, clearErrors]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  // Reset view state when projectId changes
  useEffect(() => {
    if (projectId) {
      // Reset to preview tab if the new project is previewable, otherwise code
      setActiveTab(isPreviewable ? 'preview' : 'code');
      // Reset mobile view to chat when switching projects
      setMobileView('chat');
    }
  }, [projectId, isPreviewable]);

  // Switch away from preview mode if project is not previewable
  useEffect(() => {
    if (mobileView === 'preview' && !isPreviewable) {
      setMobileView('code');
    }
    if (activeTab === 'preview' && !isPreviewable) {
      setActiveTab('code');
    }
  }, [isPreviewable, mobileView, activeTab]);

  // Close drawer when clicking outside (desktop)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isProjectsDrawerOpen) return;

      // Don't close drawer if dropdown menu is open
      if (isDropdownOpen) return;

      const target = event.target as HTMLElement;

      // Check if clicking inside drawer or trigger
      if (
        drawerRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }

      // Check if click is on a dropdown/portal element (Radix portals content outside drawer)
      const isPortalContent =
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-dropdown-menu-content]') ||
        target.closest('[role="dialog"]');

      if (isPortalContent) {
        return;
      }

      setIsProjectsDrawerOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectsDrawerOpen, isDropdownOpen]);

  const handleNewChat = () => {
    // Reset to chat view on mobile when starting new chat
    if (isMobile) {
      setMobileView('chat');
    }
    // Call the ChatPane's startNewSession function
    if (chatPaneRef.current) {
      chatPaneRef.current.startNewSession();
    }
  };

  const handleAILoadingChange = (loading: boolean) => {
    setIsAILoading(loading);
  };

  // Handle first user interaction to enable audio context
  const handleFirstInteraction = () => {
    // This will be handled automatically by the useKeepAlive hook
    // when isAILoading becomes true after user interaction
  };

  const handleDismissConsoleError = () => {
    dismissConsoleError();
  };

  const handleRequestErrorFix = useCallback((errorMessage: string) => {
    // Switch to chat view on mobile
    if (isMobile) {
      setMobileView('chat');
    }
    // Un-minimize chat on desktop if minimized
    if (isChatMinimized) {
      setIsChatMinimized(false);
    }
    // Send the error fix request through the chat
    if (chatPaneRef.current) {
      chatPaneRef.current.sendErrorFixRequest(errorMessage);
    }
  }, [isMobile, isChatMinimized]);

  const handleProjectDeleted = async () => {
    setProject(null);
    navigate('/');
  };

  const handleProjectSelect = (selectedProject: Project | null) => {
    setIsProjectsDrawerOpen(false);
    if (selectedProject) {
      setProject(selectedProject);
      navigate(`/project/${selectedProject.id}`);
    } else {
      navigate('/');
    }
  };

  if (!project && !isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Project Not Found</h1>
          <Button onClick={() => navigate('/')} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background">
        {/* Mobile Header - Same pattern as AppLayout */}
        <header className="pt-safe bg-sidebar border-b border-sidebar-border">
          <div className="h-12 px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 hover:bg-primary/10"
                onClick={() => setIsProjectsDrawerOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity"
              >
                <AfterHoursLogo className="w-6 h-6" />
                <span className="text-lg font-bold text-primary">AfterHours</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              {project ? (
                <>
                  <StarButton
                    projectId={project.id}
                    projectName={project.name}
                    className="h-8 w-8"
                  />
                  <ActionsMenu
                    projectId={project.id}
                    projectName={project.name}
                    onNewChat={handleNewChat}
                    onProjectDetails={() => setIsProjectDetailsOpen(true)}
                    isLoading={isAILoading}
                    isBuildLoading={build.isPending}
                    onFirstInteraction={handleFirstInteraction}
                  />
                </>
              ) : (
                <>
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Sidebar Overlay - positioned below header */}
        {isProjectsDrawerOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 top-[calc(env(safe-area-inset-top)+3rem)] z-30 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsProjectsDrawerOpen(false)}
            />
            {/* Sidebar */}
            <div className="fixed left-0 top-[calc(env(safe-area-inset-top)+3rem)] z-40 h-[calc(100dvh-env(safe-area-inset-top)-3rem)] w-full max-w-sm bg-sidebar border-r border-sidebar-border shadow-xl animate-in slide-in-from-left duration-200">
              <ProjectSidebar
                selectedProject={project}
                onSelectProject={handleProjectSelect}
                onClose={() => setIsProjectsDrawerOpen(false)}
                hideHeader
                isDesktopDrawer
                onDropdownOpenChange={setIsDropdownOpen}
              />
            </div>
          </>
        )}

        {/* Project Name Bar */}
        <div className="px-4 py-2 border-b bg-background/50">
          {project ? (
            <Button
              variant="ghost"
              className="p-0 h-auto text-sm font-semibold truncate hover:bg-transparent hover:text-primary"
              onClick={() => setIsProjectDetailsOpen(true)}
            >
              {project.name}
            </Button>
          ) : (
            <Skeleton className="h-5 w-32" />
          )}
        </div>

        <div className="flex-1 overflow-hidden">
          {mobileView === 'chat' && (
            project ? (
              <ChatPane
                ref={chatPaneRef}
                projectId={project.id}
                onNewChat={handleNewChat}
                onFirstInteraction={handleFirstInteraction}
                onLoadingChange={handleAILoadingChange}
                isLoading={isAILoading}
                isBuildLoading={build.isPending}
                consoleError={consoleError}
                onDismissConsoleError={handleDismissConsoleError}
              />
            ) : (
              <div className="h-full p-4 space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-32 w-full" />
              </div>
            )
          )}
          {(mobileView === 'preview' || mobileView === 'code') && (
            project ? (
              <PreviewPane
                projectId={project.id}
                activeTab={mobileView}
                projectName={project.name}
                onFirstInteraction={handleFirstInteraction}
                onRequestErrorFix={handleRequestErrorFix}
              />
            ) : (
              <div className="h-full p-4 space-y-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-full w-full" />
              </div>
            )
          )}
        </div>

        <div className="border-t border-border/50 bg-background/95 backdrop-blur-sm pb-safe">
          <div className="flex h-12">
            <button
              onClick={() => setMobileView('chat')}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors mobile-nav-button',
                mobileView === 'chat' && 'mobile-nav-button-active',
                !project && 'opacity-50 pointer-events-none'
              )}
              disabled={!project}
            >
              <MessageSquare className="h-4 w-4" />
              {t('chat')}
            </button>
            {isPreviewable && (
              <button
                onClick={() => setMobileView('preview')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors mobile-nav-button',
                  mobileView === 'preview' && 'mobile-nav-button-active',
                  !project && 'opacity-50 pointer-events-none'
                )}
                disabled={!project}
              >
                <Eye className="h-4 w-4" />
                {t('preview')}
              </button>
            )}
            <button
              onClick={() => setMobileView('code')}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 text-xs font-medium transition-colors mobile-nav-button',
                mobileView === 'code' && 'mobile-nav-button-active',
                !project && 'opacity-50 pointer-events-none'
              )}
              disabled={!project}
            >
              <Code className="h-4 w-4" />
              {t('code')}
              {mobileView !== 'code' && project && (
                <GitStatusIndicator projectId={project.id} className="ml-1" />
              )}
            </button>
          </div>
        </div>

        {/* Project Details Dialog */}
        {project && (
          <ProjectDetailsDialog
            project={project}
            open={isProjectDetailsOpen}
            onOpenChange={setIsProjectDetailsOpen}
            onProjectDeleted={handleProjectDeleted}
          />
        )}
      </div>
    );
  }

  // Desktop layout with top navbar (same pattern as AppLayout)
  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Desktop Top Navigation - Same as AppLayout */}
      <header className="sticky top-0 z-40 bg-sidebar border-b border-sidebar-border flex-shrink-0">
        <div className="h-12 px-4 flex items-center gap-1">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity mr-2"
          >
            <AfterHoursLogo className="w-6 h-6" />
            <span className="text-lg font-bold text-primary">AfterHours</span>
          </button>

          {/* Projects Dropdown Button */}
          <Button
            ref={triggerRef}
            variant={isProjectsDrawerOpen ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "h-8 gap-1 px-3 text-sm text-muted-foreground hover:text-foreground",
              isProjectsDrawerOpen && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            )}
            onClick={() => setIsProjectsDrawerOpen(!isProjectsDrawerOpen)}
          >
            <LayoutGrid className="h-4 w-4" />
            <span>Projects</span>
            {isProjectsDrawerOpen ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>

          {/* Explore Link */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-3 text-sm text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/explore')}
          >
            <Compass className="h-4 w-4" />
            <span>Explore</span>
          </Button>

          {/* Right side - spacer */}
          <div className="flex-1" />
        </div>
      </header>

      {/* Projects Drawer - positioned below navbar */}
      {isProjectsDrawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 top-12 z-30 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsProjectsDrawerOpen(false)}
          />
          {/* Drawer Panel */}
          <div
            ref={drawerRef}
            className="fixed left-0 top-12 z-40 h-[calc(100vh-3rem)] w-80 border-r border-sidebar-border bg-sidebar shadow-xl animate-in slide-in-from-left duration-200"
          >
            <ProjectSidebar
              selectedProject={project}
              onSelectProject={handleProjectSelect}
              onClose={() => setIsProjectsDrawerOpen(false)}
              hideHeader
              isDesktopDrawer
              onDropdownOpenChange={setIsDropdownOpen}
            />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Chat Panel */}
          {!isChatMinimized && (
            <ResizablePanel defaultSize={25} minSize={20}>
              <div className="h-full flex flex-col relative">
                {/* Chat Header */}
                <div className="h-12 px-4 border-b flex-shrink-0">
                  <div className="flex items-center justify-between h-12">
                    {/* Left side - Project title */}
                    <div className="flex items-center gap-3 overflow-hidden">
                      {/* Project title */}
                      <div className="flex flex-1 min-w-0 truncate">
                        {project ? (
                          <Button
                            variant="ghost"
                            className="block p-0 h-auto font-semibold text-lg truncate hover:bg-transparent hover:text-primary"
                            onClick={() => setIsProjectDetailsOpen(true)}
                          >
                            {project.name}
                          </Button>
                        ) : (
                          <Skeleton className="h-6 w-40" />
                        )}
                      </div>
                    </div>

                    {/* Right side - Star and Actions Menu */}
                    <div className="flex items-center gap-2">
                      {project ? (
                        <>
                          <StarButton
                            projectId={project.id}
                            projectName={project.name}
                            className="h-8 w-8"
                          />
                          <ActionsMenu
                            projectId={project.id}
                            projectName={project.name}
                            onNewChat={handleNewChat}
                            onProjectDetails={() => setIsProjectDetailsOpen(true)}
                            isLoading={isAILoading}
                            isBuildLoading={build.isPending}
                            onFirstInteraction={handleFirstInteraction}
                          />
                        </>
                      ) : (
                        <>
                          <Skeleton className="h-8 w-8 rounded" />
                          <Skeleton className="h-8 w-8 rounded" />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Chat Content */}
                <div className="flex-1 overflow-hidden">
                  {project ? (
                    <ChatPane
                      ref={chatPaneRef}
                      projectId={project.id}
                      onNewChat={handleNewChat}
                      onFirstInteraction={handleFirstInteraction}
                      onLoadingChange={handleAILoadingChange}
                      isLoading={isAILoading}
                      isBuildLoading={build.isPending}
                      consoleError={consoleError}
                      onDismissConsoleError={handleDismissConsoleError}
                    />
                  ) : (
                    <div className="h-full p-4 space-y-4">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-8 w-3/4" />
                      <Skeleton className="h-8 w-1/2" />
                      <Skeleton className="h-32 w-full" />
                    </div>
                  )}
                </div>
              </div>
            </ResizablePanel>
          )}

          {/* Resizable Handle */}
          {!isChatMinimized && <ResizableHandle withHandle />}

          {/* Preview/Code Panel */}
          <ResizablePanel defaultSize={isChatMinimized ? 100 : 75} minSize={30}>
            {project ? (
              <PreviewPane
                ref={previewPaneRef}
                projectId={project.id}
                activeTab={isPreviewable ? activeTab : 'code'}
                onToggleView={isPreviewable ? () => setActiveTab(activeTab === 'preview' ? 'code' : 'preview') : undefined}
                projectName={project.name}
                onFirstInteraction={handleFirstInteraction}
                isPreviewable={isPreviewable}
                isChatMinimized={isChatMinimized}
                onToggleChatMinimized={() => setIsChatMinimized(!isChatMinimized)}
                onRequestErrorFix={handleRequestErrorFix}
              />
            ) : (
              <div className="h-full p-4 space-y-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-full w-full" />
              </div>
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Project Details Dialog */}
      {project && (
        <ProjectDetailsDialog
          project={project}
          open={isProjectDetailsOpen}
          onOpenChange={setIsProjectDetailsOpen}
          onProjectDeleted={handleProjectDeleted}
        />
      )}
    </div>
  );
}
